// Mise en forme des valeurs affichées, selon la langue (fr : virgule décimale ; en : point).
import type { Alarm, InputView, OutputState } from "../api/types";
import { locale, N_, t } from "./i18n";

const formats = new Map<string, Intl.NumberFormat>();
function nf(digits: "1" | "2" | "0" | "1max"): Intl.NumberFormat {
  const key = `${locale()}:${digits}`;
  let f = formats.get(key);
  if (!f) {
    const options: Intl.NumberFormatOptions = digits === "2" ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { maximumFractionDigits: digits === "0" ? 0 : 1 };
    f = new Intl.NumberFormat(locale(), options);
    formats.set(key, f);
  }
  return f;
}
const nf1 = () => nf("1");
const nf2 = () => nf("2");

export function formatBitrate(kbps: number | null | undefined): string {
  if (!kbps || kbps <= 0) return "0 kb/s";
  if (kbps >= 1000) return `${nf2().format(kbps / 1000)} Mb/s`;
  return `${Math.round(kbps)} kb/s`;
}

export function formatNumber(n: number | null | undefined, unit = ""): string {
  if (n === null || n === undefined) return "–";
  return `${nf1().format(n)}${unit ? ` ${unit}` : ""}`;
}

/** Taille de fichier ou de disque : « 27,6 Mo », « 855 Go » (puissances de 1000, comme l'Explorateur). */
export function formatBytes(bytes: number | null | undefined): string {
  const units = [t("o"), t("ko"), t("Mo"), t("Go"), t("To")];
  if (!bytes || bytes <= 0) return `0 ${units[0]}`;
  const i = Math.min(units.length - 1, Math.floor(Math.log10(bytes) / 3));
  const v = bytes / 1000 ** i;
  return `${nf(v >= 100 ? "0" : "1max").format(v)} ${units[i]}`;
}

export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return t("{d} j {h} h", { d, h });
  if (h > 0) return `${h} h ${String(m).padStart(2, "0")} min`;
  if (m > 0) return `${m} min ${String(sec).padStart(2, "0")} s`;
  return `${sec} s`;
}

/** Heure d'un événement : « 23:05:12 », précédée de la date si ce n'est pas aujourd'hui. */
export function formatTime(ts: number, now: Date = new Date()): string {
  const d = new Date(ts * 1000);
  const time = d.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  if (d.toDateString() === now.toDateString()) return time;
  return `${d.toLocaleDateString(locale(), { day: "2-digit", month: "2-digit" })} ${time}`;
}

export type InputHealth = "online" | "degraded" | "offline" | "disabled";

/** État d'une entrée pour l'affichage : une alarme active sur une entrée en ligne la rend « dégradée ». */
export function inputHealth(input: InputView, alarms: Alarm[]): InputHealth {
  if (!input.enabled) return "disabled";
  if (!input.live.online) return "offline";
  if (alarms.some((a) => a.source === `input:${input.id}`)) return "degraded";
  return "online";
}

export const INPUT_HEALTH_LABEL: Record<InputHealth, string> = {
  online: N_("En ligne"),
  degraded: N_("Dégradé"),
  offline: N_("Pas de signal"),
  disabled: N_("Désactivée"),
};

export const OUTPUT_STATE_LABEL: Record<OutputState, string> = {
  stopped: N_("Arrêtée"),
  waiting: N_("Attente source"),
  starting: N_("Démarrage"),
  running: N_("En service"),
  retrying: N_("En relance"),
  unknown: N_("État inconnu"),
};

export const PROTOCOL_LABEL: Record<string, string> = {
  srt: "SRT", rtmp: "RTMP", rtsp: "RTSP", udp: "UDP", hls: "HLS", whip: "WHIP",
};

/** Clé aléatoire lisible (clés de flux, passphrases SRT). */
export function randomKey(length = 20): string {
  const alphabet = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

/** Protocole d'une sortie, tel qu'affiché. */
export function outputProtocolLabel(protocol: string): string {
  return protocol === "srt_listener" ? "SRT listener" : protocol.toUpperCase();   // NDI, SRT, RTMP...
}
